import React, { useState } from 'react';
import { BarChart3, PieChart } from 'lucide-react';
import { DailyVolumeChart } from './DailyVolumeChart';
import { StatusDistributionChart } from './StatusDistributionChart';

/**
 * The two charts, one card, one at a time.
 *
 * They answer different questions about the same shipments — when they came in,
 * and where they are now — and side by side they were competing for the same
 * two thirds of the screen while each one only got half of it. Stacked behind
 * tabs, whichever the user is reading gets the whole width.
 *
 * The tab bar is the product's own, down to the green glow under the active
 * one: the same markup the zone, seller and order detail pages use. This is the
 * fourth hand-written copy, which is one more than a pattern should need — see
 * the note in the plan about extracting it.
 */
const TABS = [
  {
    id: 'status',
    label: 'Distribución por Estado',
    icon: PieChart,
    subtitle: 'Click en un estado para verlo en Envíos',
  },
  {
    id: 'daily',
    label: 'Envíos por día',
    icon: BarChart3,
    subtitle: 'Apilados según en qué punto del camino está cada envío',
  },
];

export const DashboardCharts = ({
  daily,
  range,
  statusCount,
  total,
  onStatusClick,
  isLoading,
}) => {
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const current = TABS.find((tab) => tab.id === activeTab);

  return (
    <div data-testid="dashboard-charts" className="relative overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border lg:col-span-2 flex flex-col h-full">
      {/* The corner glow the detail pages use for their hero. */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-ninja-green/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />

      <div className="relative flex items-center gap-8 border-b border-border/60">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-4 text-sm font-bold transition-all relative ${
                isActive
                  ? 'text-ninja-green'
                  : 'text-text-muted hover:text-ninja-dark dark:hover:text-white'
              }`}
            >
              <Icon size={16} className={isActive ? 'text-ninja-green' : 'opacity-60'} />
              {tab.label}
              {isActive && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-ninja-green rounded-t-full shadow-[0_-2px_10px_rgba(0,192,136,0.5)]"></div>
              )}
            </button>
          );
        })}
      </div>

      <p className="relative text-sm text-text-secondary mt-4 mb-6">{current.subtitle}</p>

      {/* `min-h-0` so the chart can shrink inside the flex column instead of
          pushing the card taller than its row. */}
      <div className="relative flex-1 min-h-0 flex flex-col">
        {activeTab === 'daily' ? (
          <DailyVolumeChart daily={daily} range={range} isLoading={isLoading} />
        ) : (
          <StatusDistributionChart
            statusCount={statusCount}
            total={total}
            onStatusClick={onStatusClick}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
};
